// src/web/client/src/components/automations/EmailTypeCard.jsx
//
// ONE card per email type on the Automations page (UX brief, Phase B3): a
// title, what the email does, one switch, the timing line, the numbers, and
// the links to edit or see more. Every card on the page — this app's own two
// and the brain's two — uses this shell so they read the same.
//
// `setup` replaces the switch when the email cannot go out yet (no email set
// up, or the email is switched off in Templates). A switch that reads ON while
// nothing is sent is exactly what this exists to stop: the header shows the
// state as a label, and the card body says why and offers the one action that
// fixes it. Shape: { status, title, body, action?: { label, onClick, disabled },
// secondary?: { label, to } }.

import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';

export function Stat({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-900">{value ?? '—'}</p>
      {hint ? <p className="text-xs text-gray-400">{hint}</p> : null}
    </div>
  );
}

export default function EmailTypeCard({
  title,
  description,
  enabled,
  onToggle,
  switchDisabled = false,
  switchLabel,
  timing,
  stats = [],
  statsNote,
  links = [],
  children,
  testId,
  setup = null,
}) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-lg">{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </div>
        {setup ? (
          <span data-testid="setup-status" className="shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            {setup.status}
          </span>
        ) : onToggle ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">{enabled ? 'On' : 'Off'}</span>
            <Switch checked={Boolean(enabled)} disabled={switchDisabled} onCheckedChange={onToggle} aria-label={switchLabel || `${title} on or off`} />
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {setup ? (
          <div data-testid="setup-callout" className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-900">{setup.title}</p>
              {setup.body ? <p className="text-sm text-amber-800">{setup.body}</p> : null}
            </div>
            <div className="flex shrink-0 items-center gap-4">
              {setup.secondary ? (
                <Link to={setup.secondary.to} className="text-sm font-medium text-indigo-600 hover:text-indigo-800">{setup.secondary.label}</Link>
              ) : null}
              {setup.action ? (
                <Button size="sm" onClick={setup.action.onClick} disabled={setup.action.disabled}>{setup.action.label}</Button>
              ) : null}
            </div>
          </div>
        ) : null}
        {timing ? <p className="text-sm text-gray-600">{timing}</p> : null}
        {stats.length ? (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {stats.map((s) => <Stat key={s.label} {...s} />)}
            </div>
            {statsNote ? <p className="mt-1 text-xs text-gray-400">{statsNote}</p> : null}
          </div>
        ) : null}
        {children}
        {links.length ? (
          <div className="flex flex-wrap gap-4 text-sm">
            {links.map((l) => (l.to
              ? <Link key={l.label} to={l.to} className="font-medium text-indigo-600 hover:text-indigo-800">{l.label}</Link>
              : <button key={l.label} type="button" onClick={l.onClick} className="font-medium text-indigo-600 hover:text-indigo-800">{l.label}</button>))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
